/**
 * ChangesetDrawer — the changeset detail/lifecycle sheet (PIM Wave P5 Task
 * 7). Opened by `ChangesetsPage` (row click / "New changeset"). Fetches its
 * own detail via `useChangeset(changesetId)` (staleTime 0) so every mutation
 * below (append/remove op, preview, publish, rollback, delete) shows its
 * result immediately without the caller re-threading fresh data down as a
 * prop — mirrors why `useCategoryBindings`/`useFormSchema` are dedicated
 * queries rather than derived from a parent's already-loaded row.
 *
 * Confirms (publish / rollback / delete-draft) are INLINE, not a nested
 * `BottomSheet` — this drawer IS a `BottomSheet`, and `GroupManager.tsx`
 * (`attribute-fields/components/GroupManager.tsx`) already documents why a
 * `BottomSheet` can never nest inside another open one (Framer Motion's
 * `transform` on the outer panel breaks a descendant's `position: fixed`);
 * that file's own delete flow uses the same swap-the-button-for-a-confirm-
 * row idiom this component copies for all three of its destructive actions.
 *
 * Op-list rendering: each `OpView`'s `op_type` is exactly `"<entity_type>.
 * <action>"` (`ops.py`'s `describe_op`); rather than one i18n key PER of the
 * 27 possible `op_type` combinations (`pim.changesets.op.field.archive`,
 * `pim.changesets.op.category.restore`, ...), this composes ONE localized
 * line template from two small vocabularies — 7 entity-type labels + 7
 * action labels (`pim.changesets.op.entity.*` / `pim.changesets.op.action.*`)
 * — which also sidesteps a Russian grammatical-case trap a literal noun-
 * after-verb concatenation would hit (e.g. "Архивировать категория" is
 * wrong — accusative "категорию" is required; the line template instead
 * reads "{{action}}: {{entity}} «{{target}}»", which never inflects the
 * entity noun). Documented as a deviation from the brief's illustrative
 * `pim.changesets.op.field.archive`-shaped key in this task's report.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X } from "lucide-react";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminStates } from "@/features/admin/_shared";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { ApiError } from "@/lib/api-client";
import {
  parseChangesetRollbackConflict,
  parseChangesetTrilingualError,
  type OpView,
  type TrilingualFieldError,
} from "@/features/admin/changesets/api";
import {
  useChangeset,
  useDeleteChangeset,
  usePreviewChangeset,
  usePublishChangeset,
  useRemoveChangesetOperation,
  useRollbackChangeset,
} from "@/features/admin/changesets/hooks";
import { OpBuilderForm } from "@/features/admin/changesets/components/OpBuilderForm";
import { ImpactPreviewPanel } from "@/features/admin/changesets/components/ImpactPreviewPanel";

const ENTITY_LABEL_KEYS: Record<string, string> = {
  group: "pim.changesets.op.entity.group",
  field: "pim.changesets.op.entity.field",
  option: "pim.changesets.op.entity.option",
  binding: "pim.changesets.op.entity.binding",
  category: "pim.changesets.op.entity.category",
  alias: "pim.changesets.op.entity.alias",
  need_mapping: "pim.changesets.op.entity.need_mapping",
  // DB-level entity_type names carried by the publish 422 trilingual
  // `field_slug` ("<entity_type>:<id>") — aliased to the same op-level labels
  // so the trilingual-error rows resolve (the op list itself uses the short
  // names above from `describe_op`). Reachable via transfer-import changesets.
  attribute_field: "pim.changesets.op.entity.field",
  attribute_field_group: "pim.changesets.op.entity.group",
  attribute_field_option: "pim.changesets.op.entity.option",
};

const ACTION_LABEL_KEYS: Record<string, string> = {
  create: "pim.changesets.op.action.create",
  update: "pim.changesets.op.action.update",
  archive: "pim.changesets.op.action.archive",
  unarchive: "pim.changesets.op.action.unarchive",
  delete: "pim.changesets.op.action.delete",
  restore: "pim.changesets.op.action.restore",
  reorder: "pim.changesets.op.action.reorder",
};

const STATUS_TONE: Record<string, "neutral" | "primary" | "success"> = {
  draft: "neutral",
  published: "success",
  rolled_back: "primary",
};

export interface ChangesetDrawerProps {
  changesetId: string | null;
  onClose: () => void;
}

export function ChangesetDrawer({ changesetId, onClose }: ChangesetDrawerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();

  const { data: cs, isLoading, isError } = useChangeset(changesetId);

  const removeOpMutation = useRemoveChangesetOperation();
  const previewMutation = usePreviewChangeset();
  const publishMutation = usePublishChangeset();
  const rollbackMutation = useRollbackChangeset();
  const deleteMutation = useDeleteChangeset();

  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [confirmingRollback, setConfirmingRollback] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [trilingualErrors, setTrilingualErrors] = useState<TrilingualFieldError[] | null>(null);
  const [rollbackConflict, setRollbackConflict] = useState<string | null>(null);

  const resetConfirms = (): void => {
    setConfirmingPublish(false);
    setConfirmingRollback(false);
    setConfirmingDelete(false);
  };

  const handleClose = (): void => {
    resetConfirms();
    setTrilingualErrors(null);
    setRollbackConflict(null);
    onClose();
  };

  const describeOp = (op: OpView): string => {
    const entity = t(ENTITY_LABEL_KEYS[op.entity_type] ?? "pim.changesets.op.entity.unknown");
    const action = t(ACTION_LABEL_KEYS[op.action] ?? "pim.changesets.op.action.unknown");
    return t("pim.changesets.op.line", { action, entity, target: op.target || "—" });
  };

  const handleRemoveOp = (index: number): void => {
    if (!cs) return;
    removeOpMutation.mutate(
      { id: cs.id, index },
      {
        onSuccess: () => toast.success(t("pim.changesets.drawer.removeOpSuccess")),
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  const handlePreview = (): void => {
    if (!cs) return;
    previewMutation.mutate(cs.id, {
      onSuccess: () => toast.success(t("pim.changesets.drawer.previewSuccess")),
      onError: () => toast.error(t("error.generic")),
    });
  };

  const handlePublish = (): void => {
    if (!cs) return;
    setTrilingualErrors(null);
    publishMutation.mutate(cs.id, {
      onSuccess: () => {
        toast.success(t("pim.changesets.drawer.publishSuccess"));
        setConfirmingPublish(false);
      },
      onError: (err) => {
        const fields = parseChangesetTrilingualError(err);
        if (fields && fields.length > 0) {
          setTrilingualErrors(fields);
          setConfirmingPublish(false);
          return;
        }
        toast.error(err instanceof ApiError ? err.problem.detail ?? t("error.generic") : t("error.generic"));
      },
    });
  };

  const handleRollback = (): void => {
    if (!cs) return;
    setRollbackConflict(null);
    rollbackMutation.mutate(cs.id, {
      onSuccess: () => {
        toast.success(t("pim.changesets.drawer.rollbackSuccess"));
        setConfirmingRollback(false);
      },
      onError: (err) => {
        const detail = parseChangesetRollbackConflict(err);
        if (detail) {
          setRollbackConflict(detail);
          setConfirmingRollback(false);
          return;
        }
        toast.error(t("error.generic"));
      },
    });
  };

  const handleDelete = (): void => {
    if (!cs) return;
    deleteMutation.mutate(cs.id, {
      onSuccess: () => {
        toast.success(t("pim.changesets.drawer.deleteSuccess"));
        handleClose();
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  const title = cs ? (
    <div className="flex flex-wrap items-center gap-2">
      <span>{cs.title}</span>
      <GPill tone={STATUS_TONE[cs.status] ?? "neutral"} size="sm">
        {t(`pim.changesets.status.${cs.status}`)}
      </GPill>
    </div>
  ) : (
    t("pim.changesets.drawer.titleFallback")
  );

  return (
    <BottomSheet open={changesetId !== null} onClose={handleClose} title={title} desktop={isDesktop}>
      <AdminStates
        isLoading={isLoading}
        isError={isError}
        isEmpty={false}
        loadingLabel={t("loading")}
        emptyLabel=""
        errorLabel={t("error.loadFailed")}
        testid="pim-changeset-drawer"
      >
        {cs ? (
          <div className="flex flex-col gap-4" data-testid="pim-changeset-drawer-content">
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-[var(--g-primary-ink)]">
                {t("pim.changesets.drawer.opsTitle")}
              </h3>
              {cs.operations.length === 0 ? (
                <p className="text-sm text-[var(--g-secondary)]">{t("pim.changesets.drawer.opsEmpty")}</p>
              ) : (
                <ul className="flex flex-col gap-1.5" data-testid="pim-changeset-op-list">
                  {cs.operations.map((op, index) => (
                    <li
                      key={`${op.op_type}-${index}`}
                      className="flex items-center justify-between gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] px-3 py-2 text-sm"
                    >
                      <span>{describeOp(op)}</span>
                      {cs.status === "draft" ? (
                        <button
                          type="button"
                          data-testid={`pim-changeset-op-remove-${index}`}
                          aria-label={t("pim.changesets.drawer.removeOp")}
                          onClick={() => handleRemoveOp(index)}
                          disabled={removeOpMutation.isPending}
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <X size={14} strokeWidth={1.75} aria-hidden />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {cs.status === "draft" ? (
              <>
                <OpBuilderForm changesetId={cs.id} />

                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    data-testid="pim-changeset-preview"
                    loading={previewMutation.isPending}
                    disabled={cs.operations.length === 0}
                    onClick={handlePreview}
                    className="min-h-[44px]"
                  >
                    {t("pim.changesets.drawer.previewButton")}
                  </Button>
                </div>

                {cs.impact_snapshot ? <ImpactPreviewPanel impact={cs.impact_snapshot} /> : null}

                {trilingualErrors && trilingualErrors.length > 0 ? (
                  <div
                    role="alert"
                    data-testid="pim-changeset-trilingual-errors"
                    className="flex flex-col gap-1 rounded-[var(--g-radius-sm)] border border-[var(--g-error)] bg-[var(--g-error)]/10 p-3 text-sm text-[var(--g-error)]"
                  >
                    <span className="font-medium">{t("pim.changesets.drawer.trilingualErrorTitle")}</span>
                    <ul className="flex flex-col gap-1">
                      {trilingualErrors.map((field, index) => (
                        <li key={index}>
                          {t("pim.changesets.drawer.trilingualErrorRow", {
                            entity: t(ENTITY_LABEL_KEYS[field.entity_type] ?? "pim.changesets.op.entity.unknown"),
                            id: field.entity_id,
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--g-hairline)] pt-3">
                  {confirmingDelete ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-[var(--g-secondary)]">
                        {t("pim.changesets.drawer.deleteConfirmBody")}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmingDelete(false)}
                        disabled={deleteMutation.isPending}
                        className="min-h-[44px]"
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        data-testid="pim-changeset-delete-confirm"
                        loading={deleteMutation.isPending}
                        onClick={handleDelete}
                        className="min-h-[44px]"
                      >
                        {t("pim.changesets.drawer.deleteConfirm")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid="pim-changeset-delete"
                      onClick={() => setConfirmingDelete(true)}
                      className="min-h-[44px]"
                    >
                      {t("pim.changesets.drawer.deleteDraft")}
                    </Button>
                  )}

                  {confirmingPublish ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-[var(--g-secondary)]">
                        {t("pim.changesets.drawer.publishConfirmBody")}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmingPublish(false)}
                        disabled={publishMutation.isPending}
                        className="min-h-[44px]"
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        data-testid="pim-changeset-publish-confirm"
                        loading={publishMutation.isPending}
                        onClick={handlePublish}
                        className="min-h-[44px]"
                      >
                        {t("pim.changesets.drawer.publishConfirm")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      data-testid="pim-changeset-publish"
                      disabled={cs.operations.length === 0}
                      onClick={() => setConfirmingPublish(true)}
                      className="min-h-[44px]"
                    >
                      {t("pim.changesets.drawer.publishButton")}
                    </Button>
                  )}
                </div>
              </>
            ) : null}

            {cs.status === "published" ? (
              <div className="flex flex-col gap-2 border-t border-[var(--g-hairline)] pt-3">
                {rollbackConflict ? (
                  <p role="alert" className="text-sm text-[var(--g-error)]" data-testid="pim-changeset-rollback-conflict">
                    {rollbackConflict}
                  </p>
                ) : null}
                <div className="flex justify-end">
                  {confirmingRollback ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-[var(--g-secondary)]">
                        {t("pim.changesets.drawer.rollbackConfirmBody")}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmingRollback(false)}
                        disabled={rollbackMutation.isPending}
                        className="min-h-[44px]"
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        data-testid="pim-changeset-rollback-confirm"
                        loading={rollbackMutation.isPending}
                        onClick={handleRollback}
                        className="min-h-[44px]"
                      >
                        {t("pim.changesets.drawer.rollbackConfirm")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      data-testid="pim-changeset-rollback"
                      onClick={() => setConfirmingRollback(true)}
                      className="min-h-[44px]"
                    >
                      {t("pim.changesets.drawer.rollbackButton")}
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </AdminStates>
    </BottomSheet>
  );
}

export default ChangesetDrawer;
